import { useQuery } from '@apollo/client';
import { track } from '@bnnvara/analytics/src/track';
import { SliderCard } from '@bnnvara/cards';
import { Article } from '@bnnvara/icons/src/components/UserInterfaceIcons/Article';
import { Audio } from '@bnnvara/icons/src/components/UserInterfaceIcons/Audio';
import { Play } from '@bnnvara/icons/src/components/UserInterfaceIcons/Play';
import PropTypes from 'prop-types';
import React, { memo, useContext, useEffect, useState } from 'react';

import NPOEventContext from '../../context/NPOVideoPlayerContext';
import getRelatedRecommendations from '../../graphql/queries/recommendation.graphql';
import { createUrl, resizeImageSource } from '../../util';
import { Icon } from '../Icon';
import serializeRecommendations from './helpers/serializeRecommendations';
import * as S from './styled';

const responsive = [
  {
    breakpoint: 320,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToShow: 2,
      slidesToScroll: 1,
    },
  },
  {
    breakpoint: 1024,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
    },
  },
  {
    breakpoint: 1440,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
    },
  },
];

const RecommendationSlider = ({ id }) => {
  const { emitter } = useContext(NPOEventContext);
  const [isClient, setClientState] = useState(false);
  const [sliderIsVisible, setSliderIsVisible] = useState(false);

  // this is needed because of this issue in react-slick: https://github.com/akiran/react-slick/issues/1245
  useEffect(() => {
    setClientState(true);
  }, [setClientState]);

  useEffect(() => {
    const offs = [];
    offs.push(emitter.on('NPO_RESUME', () => setSliderIsVisible(false)));
    offs.push(emitter.on('NPO_PAUSE', () => setSliderIsVisible(true)));

    return () => {
      offs.forEach(emitter.off);
    };
  }, [emitter]);

  const { loading, error, data } = useQuery(getRelatedRecommendations, {
    variables: {
      id,
      paginator: {
        limit: 4,
      },
    },
  });

  const icons = {
    video: <Play />,
    audio: <Audio />,
    article: <Article />,
  };

  const recommendations = data ? data.recommendation.edges : [];

  // Return empty if one of these is true
  if (loading || error || recommendations === null || !sliderIsVisible) {
    return null;
  }

  const recommendationsSerialized = serializeRecommendations(recommendations);

  return (
    <S.Wrapper>
      <S.HeaderSlider>
        <S.Title>Meer over dit onderwerp</S.Title>
        <S.HideButton
          onClick={() => {
            setSliderIsVisible(false);
            track('video-player', 'click', `sluiten-related-content-cards`);
          }}
        >
          <Icon icon="cross" />
        </S.HideButton>
      </S.HeaderSlider>

      <div>
        <S.SliderStyled
          isBrand
          speed={500}
          key={isClient ? 'client' : 'server'}
          responsive={isClient ? responsive : null}
          infinite={recommendationsSerialized.length > 3}
        >
          {recommendationsSerialized.map((item, index) => (
            <a
              href={createUrl(item.brandName, item.mediaType, item.slug)}
              key={item.id}
              category="brandSlider"
              label={`slide-${index}`}
              // eslint-disable-next-line react/jsx-no-target-blank
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track(
                  'video-player',
                  'click',
                  `doorverwijs-related-type-${item.mediaType}`
                )
              }
            >
              <SliderCard
                title={item.title}
                thumbnail={
                  item.thumbnail
                    ? resizeImageSource(item.thumbnail.url, 300, 16 / 9)
                    : 'data:image/png;base64, iVBORw0KGgoAAAANSUhEUgAAAfQAAAABCAQAAACC95QfAAAAEUlEQVR42mNkGAWjYBQMewAAB9IAAi/SD5wAAAAASUVORK5CYII='
                }
                brandName={item.brandName
                  .toLowerCase()
                  .replace(/^\w/, (c) => c.toUpperCase())}
                icon={icons[item.mediaType.toLowerCase()]}
              />
            </a>
          ))}
        </S.SliderStyled>
      </div>
    </S.Wrapper>
  );
};

RecommendationSlider.propTypes = {
  id: PropTypes.string.isRequired,
};

// We are using memo here because we don't want the RecommendationSlider to rerender on every parent render
export default memo(RecommendationSlider);
